// src/pages/championships/SumulaGenerator.jsx

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import detailService from '../../services/detailService';
import championshipService from '../../services/championshipService';
import './SumulaGenerator.css';

const translatePhaseToPortuguese = (phase) => {
  if (!phase) return '';
  const phases = {
    'group': 'Fase de Grupos',
    'roundOf16': 'Oitavas de Final',
    'quarterFinal': 'Quartas de Final',
    'quarterFinals': 'Quartas de Final',
    'semiFinal': 'Semifinal',
    'semiFinals': 'Semifinal',
    'thirdPlace': 'Disputa de 3º Lugar',
    'final': 'Final'
  };
  return phases[phase] || phase;
};

const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
  return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

const SumulaGenerator = () => {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [teamALogo, setTeamALogo] = useState('/default-team-badge.png');
  const [teamBLogo, setTeamBLogo] = useState('/default-team-badge.png');
  const [championshipLogo, setChampionshipLogo] = useState('/default-championship-logo.png');
  const [championshipHeaderImage, setChampionshipHeaderImage] = useState('');
  const [championshipName, setChampionshipName] = useState(''); // 🟢 NOVO ESTADO PARA O NOME DA COPA

  useEffect(() => {
    const fetchMatchData = async () => {
      if (!matchId) {
        setError("ID da partida não fornecido.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const matchData = await detailService.getMatchDetails(matchId);
        setMatch(matchData);

        if (matchData.championship) {
          const championshipId = matchData.championship._id || matchData.championship;
          const champResponse = await championshipService.getById(championshipId);
          const championshipData = champResponse.data || champResponse;
          
          if (championshipData.name) {
            setChampionshipName(championshipData.name); // 🟢 CAPTURA O NOME DA COPA
          }
          if (championshipData.logo) {
            setChampionshipLogo(getImageUrl(championshipData.logo));
          }
          if (championshipData.headerImage) {
            setChampionshipHeaderImage(getImageUrl(championshipData.headerImage));
          }
        }

        if (matchData.teamA?.badge) {
          setTeamALogo(getImageUrl(matchData.teamA.badge));
        }
        if (matchData.teamB?.badge) {
          setTeamBLogo(getImageUrl(matchData.teamB.badge));
        }

      } catch (err) {
        setError(err.message || "Erro desconhecido ao buscar dados da partida.");
        console.error('Erro ao carregar detalhes da partida (SumulaGenerator):', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMatchData();
  }, [matchId]);

  const getMatchStageInfoString = (matchData) => {
    if (!matchData) return "FASE/RODADA";
    const isKnockoutPhase = matchData.phase && ['roundof16', 'quarterfinal', 'quarterfinals', 'semifinal', 'semifinals', 'thirdplace', 'final'].includes(matchData.phase.toLowerCase());
    if (isKnockoutPhase) {
      return translatePhaseToPortuguese(matchData.phase);
    } 
    if (matchData.round) {
      const groupInfo = matchData.group ? ` (${matchData.group})` : '';
      return `RODADA ${matchData.round}${groupInfo}`;
    }
    return "FASE INDEFINIDA";
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '' : date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };
  
  const handleLogoError = (e) => {
    e.target.src = '/default-championship-logo.png';
    e.target.onerror = null;
  };
  
  const handleTeamLogoError = (e) => {
    e.target.src = '/default-team-badge.png';
    e.target.onerror = null;
  };

  if (loading) return <div className="loading-sumula">Carregando Súmula...</div>;
  if (error) return <div className="error-sumula">Erro ao carregar súmula: {error}</div>;
  if (!match) return <div className="error-sumula">Dados da partida não encontrados.</div>;
  
  const findCoachForSignature = (lineup) => {
    const coachItem = lineup?.find(item => {
      const position = item.position?.toLowerCase(); 
      return position === 'técnico' || position === 'tecnico';
    });
    return coachItem?.player?.name || '';
  };
  
  const tecnicoHomeName = findCoachForSignature(match.lineups?.teamA);
  const tecnicoAwayName = findCoachForSignature(match.lineups?.teamB);

  const getStaffMembers = (lineup, teamId, rolesToFilter) => {
    return (lineup
      ?.filter(item => {
        const position = item.position?.toLowerCase(); 
        return rolesToFilter.includes(position);
      })
      .map(item => ({
        id: item.player?._id,
        name: (item.player?.name || '').toUpperCase(),
        shirtNumber: item.shirtNumber || '', 
        cards: match.cards?.filter(c => c.player?._id === item.player?._id && c.team === teamId) || [],
        suspended: item.suspended || false,
        positionRole: item.position
      })) || [])
      .sort((a,b) => a.name.localeCompare(b.name));
  };

  const getProcessedPlayers = (lineup, teamId) => {
    const playerRoles = ['goleiro', 'zagueiro', 'lateral', 'volante', 'meia', 'atacante'];
    return (lineup
      ?.filter(item => {
        const position = item.position?.toLowerCase(); 
        return playerRoles.includes(position);
      })
      .map(item => ({
        id: item.player?._id,
        name: (item.player?.name || 'Jogador Desconhecido').toUpperCase(),
        shirtNumber: item.shirtNumber || '',
        cards: match.cards?.filter(c => c.player?._id === item.player?._id && c.team === teamId) || [],
        suspended: item.suspended || false
      })) || [])
      .sort((a,b) => (parseInt(a.shirtNumber) || 999) - (parseInt(b.shirtNumber) || 999) || a.name.localeCompare(b.name));
  };

  const homePlayers = getProcessedPlayers(match.lineups?.teamA, match.teamA?._id);
  const awayPlayers = getProcessedPlayers(match.lineups?.teamB, match.teamB?._id);
  
  const homeAssistantCoaches = getStaffMembers(match.lineups?.teamA, match.teamA?._id, ['aux. técnico', 'auxiliar técnico']);
  const awayAssistantCoaches = getStaffMembers(match.lineups?.teamB, match.teamB?._id, ['aux. técnico', 'auxiliar técnico']);

  const homeDirectors = getStaffMembers(match.lineups?.teamA, match.teamA?._id, ['diretor']);
  const awayDirectors = getStaffMembers(match.lineups?.teamB, match.teamB?._id, ['diretor']);

  const minPlayerRows = 14;
  const minStaffRows = 4;

  const maxRegularPlayers = Math.max(homePlayers.length, awayPlayers.length, minPlayerRows);
  const maxStaffMembers = Math.max(
    homeAssistantCoaches.length + homeDirectors.length, 
    awayAssistantCoaches.length + awayDirectors.length, 
    minStaffRows
  );

  const getGoalsForPeriodCells = (teamId, isSecondHalf) => {
    const goalsData = (match.goals?.filter(g => {
      const teamMatch = g.team.toString() === teamId.toString();
      if (!teamMatch) return false;
      if (!g.minute) return !isSecondHalf;
      const minuteStr = String(g.minute);
      const minuteNumber = parseInt(minuteStr.split('+')[0]);
      const periodCondition = isSecondHalf
        ? minuteNumber > 45 || (minuteNumber === 45 && minuteStr.includes('+'))
        : minuteNumber <= 45 && !minuteStr.includes('+');
      return periodCondition;
    }) || [])
    .sort((a, b) => (parseInt(a.minute) || 0) - (parseInt(b.minute) || 0));

    return Array.from({ length: 9 }).map((_, i) => {
      const goal = goalsData[i];
      const playerName = goal?.player?.nickname || goal?.player?.name?.split(' ')[0] || '';
      const goalText = goal ? `${playerName}${goal.minute ? ' ' + goal.minute + "'" : ''}` : '';
      return (
        <td key={`goal-cell-${teamId}-${isSecondHalf ? '2nd' : '1st'}-${i}`} className="fg-data-cell" style={{fontSize: '6pt', height: '15px'}}>
          {goalText}
        </td>
      );
    });
  };

  const renderScorersList = (teamId) => {
    const teamGoals = match.goals?.filter(g => g.team.toString() === teamId.toString()) || [];
    if (teamGoals.length === 0) return <span>---</span>;

    const grouped = teamGoals.reduce((acc, g) => {
      const pId = g.player?._id;
      if (!acc[pId]) {
        const lineupKey = teamId.toString() === match.teamA._id.toString() ? 'teamA' : 'teamB';
        acc[pId] = {
          name: (g.player?.name || 'Inscrito').toUpperCase(),
          nickname: g.player?.nickname,
          shirt: match.lineups?.[lineupKey]?.find(l => l.player?._id === pId)?.shirtNumber || 'SN',
          minutes: []
        };
      }
      if (g.minute) acc[pId].minutes.push(g.minute + "'");
      return acc;
    }, {});

    return Object.values(grouped).map((p, i) => {
      const displayName = p.nickname ? `${p.name} (${p.nickname})` : p.name;
      const mins = p.minutes.length > 0 ? ` [${p.minutes.join(', ')}]` : '';
      return <div key={i} style={{marginBottom: '2px'}}>nº {p.shirt} - {displayName}{mins}</div>;
    });
  };

  const teamAName = (match.teamA?.name || 'TIME CASA').toUpperCase();
  const teamBName = (match.teamB?.name || 'TIME VISITANTE').toUpperCase();
  const scoreA = match.scoreA !== null && match.scoreA !== undefined ? match.scoreA : '';
  const scoreB = match.scoreB !== null && match.scoreB !== undefined ? match.scoreB : '';
  const phaseInfo = getMatchStageInfoString(match).toUpperCase();
  
  const locationInfo = 
    (match.stadium?.name ? `${match.stadium.name}${match.stadium.city ? `, ${match.stadium.city}` : ''}` : null) ||
    match.championship?.location ||
    match.location ||
    '';
  const finalLocation = locationInfo.toUpperCase();
  const refereeName = (match.referee?.name || '').toUpperCase();
  const delegateName = (match.delegate?.name || '').toUpperCase();

  return (
    <div className="sumula-page-container">
      <div className="sumula-document">
        
        <div className="sumula-top-logo-container">
          {championshipHeaderImage ? (
            <div className="custom-sumula-header">
                <img src={championshipLogo} alt="Logo" className="sumula-logo-embedded" onError={handleLogoError} />
                <img src={championshipHeaderImage} alt="Header" className="sumula-header-image" onError={handleLogoError} />
            </div>
          ) : (
            <img src={championshipLogo} alt="Logo" className="sumula-top-logo" onError={handleLogoError} />
          )}
        </div>

        <table className="sumula-header-table">
          <tbody>
            {/* 🟢 ADICIONADO: LINHA COM O NOME DA COPA EM DESTAQUE */}
            {championshipName && (
              <tr>
                <td colSpan="6" className="header-championship-title-row">
                  CAMPEONATO: <strong>{championshipName.toUpperCase()}</strong>
                </td>
              </tr>
            )}
            <tr>
              <td className="header-phase" colSpan="2">{phaseInfo}</td>
              <td className="header-escore-label" colSpan="4">SCORE</td>
            </tr>
            <tr>
              <td className="header-game-number">Jogo: <strong>{}</strong></td>
              <td className="header-team-a">{teamAName}</td>
              <td className="header-score-a"><strong>{scoreA}</strong></td>
              <td className="header-score-vs"><strong>X</strong></td>
              <td className="header-score-b"><strong>{scoreB}</strong></td>
              <td className="header-team-b">{teamBName}</td>
            </tr>
            <tr>
              <td className="header-date" colSpan="2">Data: <strong>{formatDate(match.date)}</strong></td>
              <td className="header-time" colSpan="2">Horário: <strong>{formatTime(match.date)}</strong></td>
              <td className="header-location" colSpan="2">Local: <strong>{finalLocation}</strong></td>
            </tr>
            <tr>
              <td className="header-referee" colSpan="3">Árbitro: {refereeName}</td>
              <td className="header-delegate" colSpan="3">Mesário: {delegateName}</td>
            </tr>
            <tr>
              <td colSpan="6"><strong>Árbitro Principal:</strong> {refereeName || ''}</td>
            </tr>
          </tbody>
        </table>

        {/* Restante da tabela de jogadores, assinaturas, faltas, gols, relatório, etc. permanece igual */}
        <table className="sumula-players-table">
          <colgroup>
            <col className="col-card-a-v" /><col className="col-card-a-v" /><col className="col-shirtno" /><col className="col-playername" />
            <col className="col-card-a-v" /><col className="col-card-a-v" /><col className="col-shirtno" /><col className="col-playername" />
          </colgroup>
          <thead>
            <tr>
              <th colSpan="3" className="players-table-header left-team-header">CARTÕES</th>
              <th className="players-table-header player-name-main-header">
                <div className="team-header-with-logo">
                  <img src={teamALogo} alt="" className="header-team-logo" onError={handleTeamLogoError} />
                  <span>{teamAName}</span>
                </div>
              </th>
              <th colSpan="3" className="players-table-header right-team-header">CARTÕES</th>
              <th className="players-table-header player-name-main-header">
                <div className="team-header-with-logo">
                  <img src={teamBLogo} alt="" className="header-team-logo" onError={handleTeamLogoError} />
                  <span>{teamBName}</span>
                </div>
              </th>
            </tr>
            <tr>
              <th className="card-col-header">A</th><th className="card-col-header">V</th><th className="shirtno-col-header">Nº</th><th className="player-name-sub-header">NOME DOS JOGADORES</th>
              <th className="card-col-header">A</th><th className="card-col-header">V</th><th className="shirtno-col-header">Nº</th><th className="player-name-sub-header">NOME DOS JOGADORES</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: maxRegularPlayers }).map((_, index) => {
              const playerA = homePlayers[index] || { name: '', shirtNumber: '', cards: [], suspended: false };
              const playerB = awayPlayers[index] || { name: '', shirtNumber: '', cards: [], suspended: false };
              const yellowA = playerA.cards.some(c => c.type === 'yellow' || c.type === 'second_yellow');
              const redA = playerA.cards.some(c => c.type === 'red' || c.type === 'second_yellow');
              const yellowB = playerB.cards.some(c => c.type === 'yellow' || c.type === 'second_yellow');
              const redB = playerB.cards.some(c => c.type === 'red' || c.type === 'second_yellow');

              return (
                <tr key={`player-row-${index}`} className={playerA.suspended || playerB.suspended ? 'player-row-with-suspended' : ''}>
                  <td className="card-cell">{yellowA ? 'X' : ''}</td>
                  <td className="card-cell">{redA ? 'X' : ''}</td>
                  <td className="shirt-no-cell">{playerA.shirtNumber}</td>
                  <td className={`player-name-cell ${playerA.suspended ? 'suspended' : ''}`}>{playerA.name}</td>
                  <td className="card-cell">{yellowB ? 'X' : ''}</td>
                  <td className="card-cell">{redB ? 'X' : ''}</td>
                  <td className="shirt-no-cell">{playerB.shirtNumber}</td>
                  <td className={`player-name-cell ${playerB.suspended ? 'suspended' : ''}`}>{playerB.name}</td>
                </tr>
              );
            })}
            
            {(maxStaffMembers > 0) && (
                <tr><td colSpan="8" style={{ borderBottom: '0.5pt solid #666', height: '2mm', padding: 0, background: '#f8f8f8'}}></td></tr>
            )}
            
            {Array.from({ length: maxStaffMembers }).map((_, index) => {
              const allHomeStaff = [...homeAssistantCoaches, ...homeDirectors];
              const allAwayStaff = [...awayAssistantCoaches, ...awayDirectors];

              const staffA = allHomeStaff[index] || { name: '', shirtNumber: '', cards: [], suspended: false, positionRole: '' };
              const staffB = allAwayStaff[index] || { name: '', shirtNumber: '', cards: [], suspended: false, positionRole: '' };

              const yellowA = staffA.cards.some(c => c.type === 'yellow' || c.type === 'second_yellow');
              const redA = staffA.cards.some(c => c.type === 'red' || c.type === 'second_yellow');
              const yellowB = staffB.cards.some(c => c.type === 'yellow' || c.type === 'second_yellow');
              const redB = staffB.cards.some(c => c.type === 'red' || c.type === 'second_yellow');

              const getRoleLabel = (positionRole) => {
                const normalizedPosition = positionRole?.toLowerCase();
                if (normalizedPosition === 'auxiliar técnico' || normalizedPosition === 'aux. técnico') return 'AUX. TÉC:';
                if (normalizedPosition === 'diretor') return 'DIRETOR:';
                return '';
              };

              return (
                <tr key={`staff-row-${index}`} className={staffA.suspended || staffB.suspended ? 'player-row-with-suspended' : ''}>
                  <td className="card-cell">{yellowA ? 'X' : ''}</td>
                  <td className="card-cell">{redA ? 'X' : ''}</td>
                  <td className="shirt-no-cell">{staffA.shirtNumber}</td>
                  <td className={`player-name-cell staff-member ${staffA.suspended ? 'suspended' : ''}`}>
                    {staffA.name ? (
                      <><span className="role-label">{getRoleLabel(staffA.positionRole)}</span>{staffA.name}</>
                    ) : ''}
                  </td>
                  <td className="card-cell">{yellowB ? 'X' : ''}</td>
                  <td className="card-cell">{redB ? 'X' : ''}</td>
                  <td className="shirt-no-cell">{staffB.shirtNumber}</td>
                  <td className={`player-name-cell staff-member ${staffB.suspended ? 'suspended' : ''}`}>
                    {staffB.name ? (
                      <><span className="role-label">{getRoleLabel(staffB.positionRole)}</span>{staffB.name}</>
                    ) : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        <table className="sumula-signatures-table">
            <tbody>
                <tr>
                    <td className="signature-label">Nº e Ass. Capitão:</td><td className="signature-line"></td><td className="signature-spacer"></td>
                    <td className="signature-label">Nº e Ass. Capitão:</td><td className="signature-line"></td>
                </tr>
                <tr>
                    <td className="signature-label">Técnico:</td><td className="signature-line"><strong>{tecnicoHomeName}</strong></td><td className="signature-spacer"></td>
                    <td className="signature-label">Técnico:</td><td className="signature-line"><strong>{tecnicoAwayName}</strong></td>
                </tr>
            </tbody>
        </table>

        <table className="sumula-fouls-goals-table">
            <colgroup>
                <col className="fg-col-period-label"/>
                {Array.from({length: 7}).map((_,i) => <col key={`foulColMarkA${i}`} className="fg-col-marker"/>)}
                <col className="fg-col-total"/><col className="fg-col-period-label"/>
                {Array.from({length: 7}).map((_,i) => <col key={`foulColMarkB${i}`} className="fg-col-marker"/>)}
                <col className="fg-col-total"/>
            </colgroup>
            <thead>
                <tr><th className="fg-header-event-label" colSpan="8">FALTAS</th><th className="fg-header-event-label" colSpan="8">FALTAS</th></tr>
                <tr>
                    <th className="fg-header-team-name">{teamAName}</th>
                    {Array.from({length: 7}).map((_,i) => <th key={`foulHA${i}`}>{i+1}º</th>)}
                    <th className="fg-header-total">Total</th>
                    <th className="fg-header-team-name">{teamBName}</th>
                    {Array.from({length: 7}).map((_,i) => <th key={`foulHB${i}`}>{i+1}º</th>)}
                    <th className="fg-header-total">Total</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td className="fg-row-period-label">1º Tempo</td>{Array.from({length: 7}).map((_,i) => <td key={`foulD1A${i}`} className="fg-data-cell"></td>)}<td className="fg-data-total-cell"></td>
                    <td className="fg-row-period-label">1º Tempo</td>{Array.from({length: 7}).map((_,i) => <td key={`foulD1B${i}`} className="fg-data-cell"></td>)}<td className="fg-data-total-cell"></td>
                </tr>
                <tr>
                    <td className="fg-row-period-label">2º Tempo</td>{Array.from({length: 7}).map((_,i) => <td key={`foulD2A${i}`} className="fg-data-cell"></td>)}<td className="fg-data-total-cell"></td>
                    <td className="fg-row-period-label">2º Tempo</td>{Array.from({length: 7}).map((_,i) => <td key={`foulD2B${i}`} className="fg-data-cell"></td>)}<td className="fg-data-total-cell"></td>
                </tr>
            </tbody>
        </table>

        <table className="sumula-fouls-goals-table" style={{marginTop: '5px'}}>
          <colgroup>
            <col className="fg-col-period-label"/>
            {Array.from({length: 9}).map((_,i) => <col key={`goalColMarkA${i}`} className="fg-col-marker-goal"/>)}
            <col className="fg-col-period-label"/>
            {Array.from({length: 9}).map((_,i) => <col key={`goalColMarkB${i}`} className="fg-col-marker-goal"/>)}
          </colgroup>
          <thead>
            <tr>
              <th className="fg-header-event-label" colSpan="10">GOLS - {teamAName}</th>
              <th className="fg-header-event-label" colSpan="10">GOLS - {teamBName}</th>
            </tr>
            <tr>
              <th className="fg-header-team-name">TEMPO</th>
              {Array.from({length: 9}).map((_,i) => <th key={`goalHA${i}`}>{i+1}º</th>)}
              <th className="fg-header-team-name">TEMPO</th>
              {Array.from({length: 9}).map((_,i) => <th key={`goalHB${i}`}>{i+1}º</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="fg-row-period-label">1º T</td>{getGoalsForPeriodCells(match.teamA?._id, false)}
              <td className="fg-row-period-label">1º T</td>{getGoalsForPeriodCells(match.teamB?._id, false)}
            </tr>
            <tr>
              <td className="fg-row-period-label">2º T</td>{getGoalsForPeriodCells(match.teamA?._id, true)}
              <td className="fg-row-period-label">2º T</td>{getGoalsForPeriodCells(match.teamB?._id, true)}
            </tr>
            <tr>
              <td colSpan="10" style={{verticalAlign:'top', padding:'5px', fontSize:'7pt', height:'40px', borderRight:'3px solid #fff'}}>
                <div style={{fontWeight:'bold', borderBottom:'0.5px solid #eee', marginBottom:'3px'}}>ARTILHEIROS:</div>
                {renderScorersList(match.teamA?._id)}
              </td>
              <td colSpan="10" style={{verticalAlign:'top', padding:'5px', fontSize:'7pt', height:'40px', borderLeft:'3px solid #fff'}}>
                <div style={{fontWeight:'bold', borderBottom:'0.5px solid #eee', marginBottom:'3px'}}>ARTILHEIROS:</div>
                {renderScorersList(match.teamB?._id)}
              </td>
            </tr>
          </tbody>
        </table>

        <table className="sumula-fouls-goals-table" style={{marginTop:'5px'}}>
          <colgroup>
            <col style={{width:'20%'}} />
            <col style={{width:'30%'}} />
            <col style={{width:'20%'}} />
            <col style={{width:'30%'}} />
          </colgroup>
          <thead>
            <tr>
              <th className="fg-header-event-label" colSpan="2">TEMPO TÉCNICO - {teamAName}</th>
              <th className="fg-header-event-label" colSpan="2">TEMPO TÉCNICO - {teamBName}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="fg-row-period-label" style={{textAlign:'center'}}>1º Tempo</td>
              <td className="fg-data-cell" style={{height:'18px'}}></td>
              <td className="fg-row-period-label" style={{textAlign:'center'}}>1º Tempo</td>
              <td className="fg-data-cell" style={{height:'18px'}}></td>
            </tr>
            <tr>
              <td className="fg-row-period-label" style={{textAlign:'center'}}>2º Tempo</td>
              <td className="fg-data-cell" style={{height:'18px'}}></td>
              <td className="fg-row-period-label" style={{textAlign:'center'}}>2º Tempo</td>
              <td className="fg-data-cell" style={{height:'18px'}}></td>
            </tr>
          </tbody>
        </table>

        <div className="sumula-report-container page-break">
          <div className="report-title">RELATÓRIO DE ARBITRAGEM</div>
          <div className="report-area"></div>
        </div>
      </div>

      <div className="print-button-container">
        <button onClick={() => window.print()} className="print-button">IMPRIMIR SÚMULA</button>
      </div>
    </div> 
  );
};

export default SumulaGenerator;